import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { FaqService } from 'src/app/services/faq/faq.service';
import { GeneralService } from 'src/app/services/general/general.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-faq',
  templateUrl: './add-edit-faq.component.html',
  styleUrls: ['./add-edit-faq.component.scss']
})
export class AddEditFaqComponent extends DestructorComponent implements OnInit {

  @Output() faqAdded = new EventEmitter<void>();
  @Output() faqUpdated = new EventEmitter<void>();

  faqForm!: FormGroup;
  isLoading = false;
  isEditMode = false;
  selectedFaqId: string = '';
  selectedImageFile: File | null = null;
  imagePreviewUrl: string | null = null;
  isImageUploading: boolean = false;
  uploadedImageUrl: string = '';

  constructor(
    private fb: FormBuilder,
    private _faqService: FaqService,
    private _generalService: GeneralService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.faqForm = this.fb.group({
      question: ['', [Validators.required, Validators.minLength(5)]],
      answer: ['', [Validators.required, Validators.minLength(10)]],
      image_url: [''],
      has_image: [false]
    });
  }

  // Getter methods for form controls
  get question() { return this.faqForm.get('question'); }
  get answer() { return this.faqForm.get('answer'); }
  get image_url() { return this.faqForm.get('image_url'); }
  get has_image() { return this.faqForm.get('has_image'); }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_faq_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const faqId = modalElement.getAttribute('data-faq-id');
        if (faqId) {
          this.isEditMode = true;
          this.selectedFaqId = faqId;
          this.loadFaqData(faqId);
        } else {
          this.isEditMode = false;
          this.selectedFaqId = '';
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this.resetForm();
      });
    }
  }

  private loadFaqData(faqId: string): void {
    this.isLoading = true;
    this.subscriptions.push(
      this._faqService.GetDataById(faqId).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.populateForm(response.data);
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading FAQ:', error);
          this.isLoading = false;
          Swal.fire({
            text: 'Failed to load FAQ data.',
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: 'Ok, got it!',
            customClass: {
              confirmButton: 'btn btn-primary'
            }
          });
        }
      })
    );
  }

  private populateForm(faq: any): void {
    this.faqForm.patchValue({
      question: faq.question || '',
      answer: faq.answer || '',
      image_url: faq.image_url || '',
      has_image: faq.has_image || false
    });

    // Set image preview if exists
    if (faq.image_url) {
      this.uploadedImageUrl = faq.image_url;
      this.imagePreviewUrl = faq.image_url;
      this.updateImagePreview(faq.image_url);
    }
  }

  // Handle image file selection
  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];

      // Validate file type
      const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          text: 'Please select a valid image file (PNG, JPG, JPEG)',
          icon: 'error',
          buttonsStyling: false,
          confirmButtonText: 'Ok, got it!',
          customClass: {
            confirmButton: 'btn btn-primary'
          }
        });
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        Swal.fire({
          text: 'File size must be less than 5MB',
          icon: 'error',
          buttonsStyling: false,
          confirmButtonText: 'Ok, got it!',
          customClass: {
            confirmButton: 'btn btn-primary'
          }
        });
        return;
      }

      this.selectedImageFile = file;

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.imagePreviewUrl = e.target.result;
        // Update the image preview in the DOM
        if (this.imagePreviewUrl) {
          this.updateImagePreview(this.imagePreviewUrl);
        }
      };
      reader.readAsDataURL(file);

      // Update form control
      this.faqForm.patchValue({
        image_url: file.name,
        has_image: true
      });

      // Upload the image immediately
      this.uploadImage(file);
    }
  }

  // Upload image to S3
  private uploadImage(file: File): void {
    this.isImageUploading = true;
    this._generalService.uploadFileByContent({ "contentType": file.type }).subscribe({
      next: (res) => {
        const imageUrl = res.data.url;
        const formData = this._generalService.convertKeysToFormdata(res.data.result.fields, file);

        this._generalService.sendFileToS3(res.data.result.url, formData).subscribe({
          next: () => {
            this.isImageUploading = false;
            this.uploadedImageUrl = imageUrl;
            this.faqForm.patchValue({
              image_url: imageUrl,
              has_image: true
            });
            console.log('Image uploaded successfully:', imageUrl);
          },
          error: (error) => {
            this.isImageUploading = false;
            console.error('Error uploading image to S3:', error);
            Swal.fire({
              text: "Error uploading image. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        });
      },
      error: (error) => {
        this.isImageUploading = false;
        console.error('Error getting presigned URL:', error);
        Swal.fire({
          text: "Error uploading image. Please try again.",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Ok, got it!",
          customClass: {
            confirmButton: "btn btn-primary"
          }
        });
      }
    });
  }

  // Update the image preview in the DOM
  private updateImagePreview(imageUrl: string): void {
    const imageWrapper = document.querySelector('.faq-image-preview') as HTMLElement;
    if (imageWrapper) {
      imageWrapper.style.backgroundImage = `url(${imageUrl})`;
    }
  }

  // Remove selected image
  removeImage(): void {
    this.selectedImageFile = null;
    this.imagePreviewUrl = null;
    this.uploadedImageUrl = '';
    this.faqForm.patchValue({
      image_url: '',
      has_image: false
    });

    // Reset to default image
    const imageWrapper = document.querySelector('.faq-image-preview') as HTMLElement;
    if (imageWrapper) {
      imageWrapper.style.backgroundImage = 'none';
    }

    // Clear file input
    const fileInput = document.querySelector('input[name="image_url"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  private resetForm(): void {
    this.faqForm.reset({
      question: '',
      answer: '',
      image_url: '',
      has_image: false
    });
    this.isLoading = false;
    this.selectedImageFile = null;
    this.imagePreviewUrl = null;
    this.uploadedImageUrl = '';
    this.isImageUploading = false;
    this.markFormGroupUntouched(this.faqForm);

    // Reset image preview
    const imageWrapper = document.querySelector('.faq-image-preview') as HTMLElement;
    if (imageWrapper) {
      imageWrapper.style.backgroundImage = 'none';
    }
  }

  private markFormGroupUntouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsUntouched();
      control?.markAsPristine();
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_faq_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  onSubmit(): void {
    if (this.faqForm.valid && !this.isLoading) {
      // Check if image is still uploading
      if (this.isImageUploading) {
        Swal.fire({
          text: "Please wait for the image to finish uploading.",
          icon: "warning",
          buttonsStyling: false,
          confirmButtonText: "Ok, got it!",
          customClass: {
            confirmButton: "btn btn-primary"
          }
        });
        return;
      }

      this.isLoading = true;

      const formData = this.faqForm.value;

      // Prepare submit data
      const submitData: any = {
        question: formData.question,
        answer: formData.answer,
        image_url: this.uploadedImageUrl || formData.image_url || '',
        has_image: !!this.uploadedImageUrl || !!formData.image_url
      };

      const apiCall = this.isEditMode
        ? this._faqService.UpdateData(this.selectedFaqId, submitData)
        : this._faqService.AddData(submitData);

      this.subscriptions.push(
        apiCall.subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('FAQ operation successful:', response);

            // Show success message
            Swal.fire({
              text: this.isEditMode
                ? "FAQ has been successfully updated!"
                : "FAQ has been successfully created!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                // Close the modal
                this.closeModal();

                // Emit appropriate event
                if (this.isEditMode) {
                  this.faqUpdated.emit();
                } else {
                  this.faqAdded.emit();
                }

                // Reset the form
                this.resetForm();
              }
            });
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('FAQ operation failed:', error);

            // Show error message
            Swal.fire({
              text: error.error?.message || "An error occurred. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    } else {
      this.markFormGroupTouched(this.faqForm);
    }
  }
}
